import { createFileRoute } from "@tanstack/react-router"
import { Button } from "@ui/components/primitives/Button"

// Emitted as files, not inlined, so each logo tile can link to its download.
import entireLockupDark from "@/assets/logos/entire-lockup-dark.svg?no-inline"
import entireLockupLight from "@/assets/logos/entire-lockup-light.svg?no-inline"
import entireSymbolDark from "@/assets/logos/entire-symbol-dark.svg?no-inline"
import entireSymbolLight from "@/assets/logos/entire-symbol-light.svg?no-inline"
import { marketingPages } from "@/lib/marketing-pages"
import { seoMeta } from "@/lib/seo"
import { SiteContentColumn } from "@/routes/-components/site/SiteContentColumn"
import { SiteSection } from "@/routes/-components/site/SiteSection"

export const Route = createFileRoute("/_content/brand/")({
  head: () =>
    seoMeta({
      title: "Brand",
      description: marketingPages["/brand"].description,
      url: "https://entire.com/brand",
      image: "https://entire.com/og/page/brand.png",
    }),
  component: BrandPage,
})

const usageRules = [
  "Don't rearrange the elements of the logo",
  "Don't add graphic effects like shadows or gradients",
  "Don't place the logo over busy backgrounds",
  "Don't mix the color of the logo or use backgrounds that provide insufficient contrast",
  "Don't compress, distort, skew, stretch, or alter the logo in any way",
]

function BrandAssetPair({
  title,
  description,
  darkSrc,
  lightSrc,
  imageClassName,
  imageHeight,
  imageWidth,
}: {
  title: string
  description: string
  darkSrc: string
  lightSrc: string
  imageClassName: string
  imageHeight: number
  imageWidth: number
}) {
  const fileName = title.toLowerCase().replaceAll(" ", "-")
  return (
    <SiteSection innerClassName="px-6 py-10 md:px-20">
      <SiteContentColumn gap={6}>
        <div className="flex flex-col gap-3">
          <h2 className="text-site-title font-headline text-default">{title}</h2>
          <p className="text-site-body font-sans text-muted">{description}</p>
        </div>
        <div className="grid overflow-hidden border border-subtle md:grid-cols-2">
          <a
            href={darkSrc}
            download={`${fileName}-dark.svg`}
            aria-label={`Download ${title} dark (SVG)`}
            className="flex h-64 items-center justify-center border-b border-subtle bg-white p-8 md:border-r md:border-b-0"
          >
            <img
              src={darkSrc}
              alt={`${title} dark`}
              width={imageWidth}
              height={imageHeight}
              loading="lazy"
              decoding="async"
              className={imageClassName}
            />
          </a>
          <a
            href={lightSrc}
            download={`${fileName}-light.svg`}
            aria-label={`Download ${title} light (SVG)`}
            className="flex h-64 items-center justify-center bg-neutral-900 p-8"
          >
            <img
              src={lightSrc}
              alt={`${title} light`}
              width={imageWidth}
              height={imageHeight}
              loading="lazy"
              decoding="async"
              className={imageClassName}
            />
          </a>
        </div>
      </SiteContentColumn>
    </SiteSection>
  )
}

function BrandPage() {
  return (
    <>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn>
          <div className="flex flex-col gap-3">
            <h1 className="text-site-title-lg font-headline text-default">
              Entire brand guidelines
            </h1>
            <p className="text-site-body font-sans text-muted">
              Resources to represent Entire consistently and accurately.
            </p>
          </div>
          <div className="flex">
            <Button href="/downloads/entire-brand-kit.zip" download variant="attention">
              Download Brand Assets
            </Button>
          </div>
        </SiteContentColumn>
      </SiteSection>
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn>
          <section className="flex flex-col gap-3">
            <h2 className="text-site-title font-headline text-default">Naming</h2>
            <p className="text-site-body font-sans text-muted">
              Refer to us as "Entire" in a single word, always spelled with a capital "E". It is the
              brand name of both our company and our application.
            </p>
          </section>
          <section className="flex flex-col gap-3">
            <h2 className="text-site-title font-headline text-default">Usage</h2>
            <p className="text-site-body font-sans text-muted">
              Maintain clear space around the symbol and lockup of at least 25% of its height.
            </p>
            <ul className="text-site-body list-inside list-disc space-y-2 font-sans text-muted">
              {usageRules.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
          </section>
        </SiteContentColumn>
      </SiteSection>
      <BrandAssetPair
        title="Entire Lockup"
        description="The Entire lockup should be used in all references to Entire as space allows. Monochrome usage is preferred."
        darkSrc={entireLockupDark}
        lightSrc={entireLockupLight}
        imageWidth={960}
        imageHeight={237}
        imageClassName="max-h-12 max-w-full"
      />
      <BrandAssetPair
        title="Entire Symbol"
        description="For tight layouts or logo-only grids, the Entire symbol is a concise way to refer to Entire. Use with good judgment for your audience, as the Entire lockup has stronger brand recognition."
        darkSrc={entireSymbolDark}
        lightSrc={entireSymbolLight}
        imageWidth={231}
        imageHeight={237}
        imageClassName="max-h-20 max-w-full"
      />
      <SiteSection innerClassName="px-6 py-10 md:px-20">
        <SiteContentColumn>
          <section className="flex flex-col gap-3">
            <h2 className="text-site-title font-headline text-default">Icon</h2>
            <p className="text-site-body font-sans text-muted">
              The icon file is reserved for contexts where the logo appears at very small sizes,
              32px and below. Don't scale up the icon beyond this size.
            </p>
          </section>
          <section className="flex flex-col gap-3">
            <h2 className="text-site-title font-headline text-default">Color</h2>
            <p className="text-site-body font-sans text-muted">
              Ensure sufficient contrast ratios for accessibility. When in doubt, default to
              neutrals.
            </p>
          </section>
        </SiteContentColumn>
      </SiteSection>
    </>
  )
}
